import axios from "axios";

// Empty string → same-origin (production, served from FastAPI)
// Explicit URL → separate backend (local dev or override)
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// On 401: try silent token refresh (uses the 7-day refresh_token cookie).
// Queue concurrent requests during refresh, retry them on success.
// If refresh also fails, broadcast immo:auth-expired so AuthContext can log out.
let _refreshing = false;
let _queue = [];

const drainQueue = (err) => {
  _queue.forEach((cb) => cb(err));
  _queue = [];
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    if (
      error.response?.status !== 401 ||
      cfg._retried ||
      cfg.url?.includes("/auth/")
    ) {
      return Promise.reject(error);
    }

    cfg._retried = true;

    if (_refreshing) {
      return new Promise((resolve, reject) =>
        _queue.push((err) => (err ? reject(err) : resolve(api(cfg))))
      );
    }

    _refreshing = true;
    try {
      await api.post("/auth/refresh");
      drainQueue(null);
      return api(cfg);
    } catch (refreshErr) {
      drainQueue(refreshErr);
      window.dispatchEvent(new Event("immo:auth-expired"));
      return Promise.reject(refreshErr);
    } finally {
      _refreshing = false;
    }
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
